import { useCallback, useRef } from 'react';
import { useMutation, useQueryClient, type UseMutationOptions, type QueryKey } from '@tanstack/react-query';
import type { OutboxOp, SyncableEntity } from '@erp/native/interfaces';
import { useNativeBridge } from '@/lib/native-context';
import { useSyncOptional } from '@/lib/sync-context';
import { useCurrentTenant } from '@/hooks/use-tenant';

export interface ResilientOptions<TInput, TResult>
  extends Omit<UseMutationOptions<TResult, Error, TInput>, 'mutationFn'> {
  /** Logical entity bucket for the outbox. */
  entity: SyncableEntity;
  /** CRUD verb or 'custom' (with `method` set on the entry). */
  op: OutboxOp;
  /** Custom method name when op === 'custom'. */
  method?: string;
  /** The online network call. Throws on failure. */
  online: (input: TInput) => Promise<TResult>;
  /** Synthesize an optimistic result for offline-queued writes. */
  optimistic: (input: TInput, clientId: string) => TResult;
  /** Query keys to invalidate after success / enqueue. */
  invalidate?: QueryKey[];
  /** Online write deadline before falling back to offline handling. */
  timeoutMs?: number;
  /**
   * Optional: hook into the cache to insert/update the optimistic row
   * directly. Called when the mutation queues offline. The returned
   * function receives the queryClient so the call site can write the
   * optimistic value into every list/detail query that should reflect
   * it. Without this, the offline path still works (the row is in the
   * outbox) but the user won't *see* it in the list until they
   * reconnect, which is a confusing UX.
   */
  applyOptimistic?: (
    qc: ReturnType<typeof useQueryClient>,
    result: TResult,
    input: TInput,
  ) => void;
}

/** Wait at most `ms` for `p`; reject with a synthetic timeout otherwise. */
function withTimeout<T>(p: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const id = setTimeout(() => reject(new Error('online-timeout')), ms);
    p.then(
      (v) => {
        clearTimeout(id);
        resolve(v);
      },
      (e) => {
        clearTimeout(id);
        reject(e);
      },
    );
  });
}

/**
 * Mutation wrapper with bulletproof offline behaviour.
 *
 * Flow:
 * 1. If the bridge says we're offline → queue immediately + apply
 *    optimistic update to the cache.
 * 2. If we're online → race the network call against a 5s deadline.
 *    Anything failing within that window (network error, auth refresh
 *    hang, server crash, weird thrown shape) drops us into the queue
 *    path. Better to queue a real error and surface it in the sync
 *    status page than to throw "save failed" at the user.
 * 3. On real online success → invalidate the configured keys so
 *    fresh server data replaces the optimistic row.
 *
 * The cache strategy is the difference between "I clicked save and
 * nothing happened" and "I see my new row immediately, with a small
 * label saying it's pending sync". We do NOT invalidate when queued
 * (that would refetch from a dead network and erase the optimistic
 * row); instead we use the caller-provided `applyOptimistic` to write
 * the row directly into the relevant query caches.
 */
export function useResilientMutation<TInput, TResult>(opts: ResilientOptions<TInput, TResult>) {
  const bridge = useNativeBridge();
  const sync = useSyncOptional();
  const qc = useQueryClient();
  const tenantId = useCurrentTenant().data?.id ?? '';
  const queuedInputsRef = useRef<TInput[]>([]);

  const mutationFn = useCallback(
    async (input: TInput): Promise<TResult> => {
      const isOnline = bridge.network.isOnline();
      const haveOffline = !!sync && bridge.platform.isDesktop;

      const queue = async (): Promise<TResult> => {
        const clientId = crypto.randomUUID();
        const optimisticResult = opts.optimistic(input, clientId);

        // Apply the optimistic row to the cache so the list/detail
        // pages re-render with it without waiting for a refetch.
        if (opts.applyOptimistic) {
          try {
            opts.applyOptimistic(qc, optimisticResult, input);
          } catch {
            /* don't let a buggy applyOptimistic block the mutation */
          }
        }

        if (haveOffline) {
          queuedInputsRef.current.push(input);
          await sync!.engine.enqueue({
            tenantId,
            entity: opts.entity,
            op: opts.op,
            method: opts.method,
            payload: input,
            clientId,
          });
          void bridge.notifications
            .show({
              title: 'Enregistré hors-ligne',
              body: `Opération mise en file d'attente (${opts.entity}/${opts.method ?? opts.op}).`,
              level: 'info',
            })
            .catch(() => {
              /* ignore */
            });
        }
        return optimisticResult;
      };

      if (!isOnline) return queue();

      try {
        return await withTimeout(opts.online(input), opts.timeoutMs ?? 5000);
      } catch (err) {
        // Always queue on failure when we have an offline engine.
        // Validation errors (uniqueness, RLS, etc.) get queued too —
        // the sync engine will retry and surface them in the outbox UI
        // after MAX_ATTEMPTS, which is better than blocking the user
        // with an error toast that may or may not be a real failure.
        if (haveOffline) return queue();
        throw err;
      }
    },
    [bridge, sync, tenantId, opts, qc],
  );

  return useMutation<TResult, Error, TInput>({
    ...opts,
    mutationFn,
    onSuccess: (...args) => {
      const variables = args[1] as TInput;
      const queuedIndex = queuedInputsRef.current.findIndex((queued) => Object.is(queued, variables));
      const wasQueued = queuedIndex !== -1;
      if (wasQueued) queuedInputsRef.current.splice(queuedIndex, 1);
      // Only invalidate when the mutation actually went through online.
      // We can't tell from the result alone, so we use a heuristic: if
      // the network is currently online + reachable, fresh server data
      // is worth fetching. If offline, skip invalidation to preserve
      // the optimistic row in the cache (an invalidate would trigger
      // a refetch that fails and clears the row).
      if (!wasQueued && bridge.network.isOnline()) {
        opts.invalidate?.forEach((key) => qc.invalidateQueries({ queryKey: key }));
      }
      (opts.onSuccess as ((...a: unknown[]) => unknown) | undefined)?.(...args);
    },
  });
}
